/**
 * Analytics Utility for RNB Consulting Group
 * Tracks events to Google Analytics and Meta Pixel
 */

// Google Analytics Event Tracking
export const trackGAEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Meta Pixel Event Tracking
export const trackFBEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
};

// Track both platforms at once
export const trackEvent = (eventName, parameters = {}) => {
  trackGAEvent(eventName, parameters);
  trackFBEvent(eventName, parameters);
};

// ============================================
// Pre-defined Event Trackers
// ============================================

/**
 * Track when a user views a service/program page
 */
export const trackViewContent = (contentName, contentCategory, value = 0) => {
  trackGAEvent('view_item', {
    item_name: contentName,
    item_category: contentCategory,
    value: value,
    currency: 'USD'
  });
  
  trackFBEvent('ViewContent', {
    content_name: contentName,
    content_category: contentCategory,
    value: value,
    currency: 'USD'
  });
};

/**
 * Track when a user starts a lead form (consultation, contact, etc.)
 */
export const trackLeadStart = (formName) => {
  trackGAEvent('begin_checkout', {
    item_name: formName
  });
  
  trackFBEvent('InitiateCheckout', {
    content_name: formName
  });
};

/**
 * Track when a lead form is submitted
 */
export const trackLeadSubmit = (formName, value = 0) => {
  trackGAEvent('generate_lead', {
    item_name: formName,
    value: value,
    currency: 'USD'
  });
  
  trackFBEvent('Lead', {
    content_name: formName,
    value: value,
    currency: 'USD'
  });
};

/**
 * Track consultation booking
 */
export const trackConsultationBooked = () => {
  trackGAEvent('schedule', {
    event_category: 'engagement',
    event_label: 'consultation_booked'
  });
  
  trackFBEvent('Schedule');
};

/**
 * Track program application started
 */
export const trackApplicationStart = (programName, programPrice) => {
  trackGAEvent('begin_checkout', {
    item_name: programName,
    value: programPrice,
    currency: 'USD'
  });
  
  trackFBEvent('InitiateCheckout', {
    content_name: programName,
    value: programPrice,
    currency: 'USD'
  });
};

/**
 * Track program application submitted
 */
export const trackApplicationSubmit = (programName, programPrice) => {
  trackGAEvent('add_to_cart', {
    item_name: programName,
    value: programPrice,
    currency: 'USD'
  });
  
  trackFBEvent('AddToCart', {
    content_name: programName,
    value: programPrice,
    currency: 'USD'
  });
};

/**
 * Track successful purchase/payment
 */
export const trackPurchase = (transactionId, value, itemName) => {
  trackGAEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: 'USD',
    items: [{ item_name: itemName }]
  });
  
  trackFBEvent('Purchase', {
    value: value,
    currency: 'USD',
    content_name: itemName
  });
};

/**
 * Track quote request
 */
export const trackQuoteRequest = (value) => {
  trackGAEvent('generate_lead', {
    event_category: 'engagement',
    event_label: 'quote_requested',
    value: value,
    currency: 'USD'
  });
  
  trackFBEvent('Lead', {
    content_name: 'Quote Request',
    value: value,
    currency: 'USD'
  });
};

/**
 * Track contact form submission
 */
export const trackContactSubmit = () => {
  trackGAEvent('contact', {
    event_category: 'engagement',
    event_label: 'contact_form'
  });
  
  trackFBEvent('Contact');
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (buttonName, pageName) => {
  trackGAEvent('cta_click', {
    event_category: 'engagement',
    event_label: buttonName,
    page: pageName
  });
};

/**
 * Track page scroll depth
 */
export const trackScrollDepth = (percentage, pageName) => {
  trackGAEvent('scroll', {
    percent_scrolled: percentage,
    page: pageName
  });
};

// ============================================
// Google Ads Conversion Tracking
// ============================================

/**
 * Track Google Ads conversion - Contact form submission
 */
export const trackAdsContactFormConversion = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17919592611/ZTEBCNvn2P8bEKOR3eBC',
    });
  }
};

/**
 * Track Google Ads conversion - Quote request
 */
export const trackAdsQuoteConversion = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17919592611/nwFcCNDKyP8bEKOR3eBC',
      'value': 1.0,
      'currency': 'USD',
    });
  }
};

/**
 * Track Google Ads conversion - Book consultation
 */
export const trackAdsConsultationConversion = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17919592611/YcNuCOvj0f8bEKOR3eBC',
    });
  }
};

/**
 * Track Google Ads conversion - Foundations First purchase
 */
export const trackAdsFoundationsFirstConversion = (transactionId = '') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17919592611/mcUCCMHo2_8bEKOR3eBC',
      'value': 1.0,
      'currency': 'USD',
      'transaction_id': transactionId,
    });
  }
};

export default {
  trackEvent,
  trackGAEvent,
  trackFBEvent,
  trackViewContent,
  trackLeadStart,
  trackLeadSubmit,
  trackConsultationBooked,
  trackApplicationStart,
  trackApplicationSubmit,
  trackPurchase,
  trackQuoteRequest,
  trackContactSubmit,
  trackCTAClick,
  trackScrollDepth,
  trackAdsContactFormConversion,
  trackAdsQuoteConversion,
  trackAdsConsultationConversion,
  trackAdsFoundationsFirstConversion,
};
